import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Switches which product image the cards show (product view vs model view).
   *
   * @param {Event} event
   */
  updateMediaView({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    const { grid } = this.refs;

    if (!grid) return;

    grid.setAttribute('product-media-view', target.value);

    // The desktop and mobile bars have a radio group each, so checking one leaves the other.
    for (const input of this.querySelectorAll('input[name^="media-view"]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === target.value;
    }

    // Carousel cards swap by moving to another slide, which keeps the slider usable.
    for (const carousel of grid.querySelectorAll('basic-carousel[data-model-slide]')) {
      if ('goTo' in carousel) carousel.goTo(carousel.modelSlideIndex);
    }

    requestIdleCallback(() => {
      sessionStorage.setItem('product-media-view', target.value);
    });
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    const isSafari =
      navigator.vendor?.toLowerCase().includes('apple') && !navigator.userAgent.toLowerCase().includes('chrome');

    // Skip View Transitions on Safari and force reflow
    if (isSafari) {
      this.#setLayout(value);
      // Dispatch event for carousels to refresh
      grid.dispatchEvent(new CustomEvent('grid-layout-change', { bubbles: true }));
      if (grid instanceof HTMLElement) {
        grid.style.display = 'none';
        void grid.offsetHeight;
        grid.style.display = '';
      }
    } else {
      await startViewTransition(() => {
        this.#setLayout(value);
        // Dispatch event inside View Transition so carousel refresh is part of the transition
        grid.dispatchEvent(new CustomEvent('grid-layout-change', { bubbles: true }));
      }, ['product-grid']);
    }

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
